import React, { useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { getValueByPath } from "../Meta/metaUtils";
import { getIcon } from "../../widgets/registry";


function TooltipPortal({ anchorRect, children }) {
  if (!anchorRect) return null;

  return ReactDOM.createPortal(
    <div
      style={{
        position: "fixed",
        top: anchorRect.bottom + 8,
        left: anchorRect.left + anchorRect.width / 2,
        transform: "translateX(-50%)",
        zIndex: 9999,
      }}
    >
      {children}
    </div>,
    document.body
  );
}

export default function StackedProgressStatCard({
  dataContext = {},
  theme = {},
}) {
  const props = dataContext?.props || {};
  const { items = [] } = props;

  const {
    bar = {},
    labelClass = "text-sm text-[#585858]",
    valueClass = "text-lg font-semibold",
    divider = {},
    tooltip = {},
  } = theme;

  const barHeight = bar.height ?? 32;
  const barRadius = bar.radius ?? 20;

  const dividerHeight = divider.height ?? "2rem";
  const dividerColor = divider.color ?? "#E5E7EB";

  const values = useMemo(
    () =>
      items.map(
        (item) => Number(getValueByPath(dataContext, item.path)) || 0
      ),
    [items, dataContext]
  );

  const total = useMemo(
    () => values.reduce((a, b) => a + b, 0),
    [values]
  );

  const [tooltipInfo, setTooltipInfo] = useState(null);

  return (
    <div className="bg-white rounded-xl pt-6 flex flex-col gap-6">
      {/* stacked bar */}
      <div className="relative" style={{ height: barHeight }}>
        <div
          className="w-full flex"
          style={{
            height: barHeight,
            borderRadius: barRadius,
            overflow: "hidden",
          }}
        >
          {items.map((item, idx) => {
            const value = values[idx];
            const percent = total > 0 ? (value / total) * 100 : 0;
            if (percent <= 0) return null;

            return (
              <div
                key={idx}
                style={{
                  width: `${percent}%`,
                  height: "100%",
                  backgroundColor: item.color,
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  setTooltipInfo({
                    rect: e.currentTarget.getBoundingClientRect(),
                    label: item.label,
                    value,
                    color: item.color,
                  });
                }}
                onMouseLeave={() => setTooltipInfo(null)}
              />
            );
          })}
        </div>
      </div>

      {/* tooltip */}
      <TooltipPortal anchorRect={tooltipInfo?.rect}>
        {tooltipInfo && (
          <div
            style={{
              backgroundColor: tooltipInfo.color,
              color: "#fff",
              padding: tooltip.padding ?? "6px 12px",
              borderRadius: "9999px",
              fontSize: tooltip.fontSize ?? "13px",
              fontWeight: 500,
              whiteSpace: "nowrap",
              boxShadow: "0 6px 14px rgba(0,0,0,0.15)",
              pointerEvents: "none",
            }}
          >
            {tooltipInfo.label}: {tooltipInfo.value}
          </div>
        )}
      </TooltipPortal>

      {/* Bottom stats */}
      <div className="flex items-center gap-5">
        {items.map((item, idx) => {
          const value = values[idx];
          const iconSrc = item.icon ? getIcon(item.icon) : null;

          return (
            <React.Fragment key={idx}>
              <div className="flex flex-col gap-2">
                <span className={labelClass}>{item.label}</span>

                <div className="flex items-center gap-1">
                  {iconSrc && (
                    <img
                      src={iconSrc}
                      alt={item.label}
                      className="w-4 h-4"
                    />
                  )}
                  <span
                    className={valueClass}
                    style={{ color: item.color }}
                  >
                    {value}
                  </span>
                </div>
              </div>

              {idx < items.length - 1 && (
                <div
                  style={{
                    height: dividerHeight,
                    width: 1,
                    backgroundColor: dividerColor,
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}